import React, { useEffect, useMemo, useRef } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import { formatPrice, cn } from "@/lib/utils";
import type { PriceSuggestion } from "@/types/price-insight";
import type { SellPriceBandSnapshot } from "@/lib/hooks/useSellPriceBand";
import SellerBandEnforcementPreview from "@/components/pricing/SellerBandEnforcementPreview";
import {
  capturePriceChipClicked,
  capturePriceSuggestionAccepted,
  capturePriceSuggestionDismissed,
  capturePriceSuggestionShown,
} from "@/lib/analytics/sellerPricingFriction";

export interface UserPriceInsightPanelProps {
  itemTitle: string;
  itemCategory: string;
  itemType: string;
  currentPrice?: number;
  onSuggestedPrice?: (price: number) => void;
  onClearPrice?: () => void;
  isEnabled: boolean;
  isLoading: boolean;
  error: string | null;
  data: PriceSuggestion | null;
  getPriceSuggestion: (
    title: string,
    category: string,
    type: string,
    currentPrice?: number,
    options?: { listingAttributes?: Record<string, unknown> | null },
  ) => void;
  clearSuggestion: () => void;
  /** Phase 4: mandatory band display (auto-fetched by parent). */
  enforcementMode?: boolean;
  bandLoading?: boolean;
  bandError?: string | null;
  band?: SellPriceBandSnapshot | null;
  /** Admin setting: show clickable mid / quick-sale chips (requires enforcement). */
  bandRecommendationChipsEnabled?: boolean;
  /** Structured attrs for attribute-first cohort (e.g. sewing_machine_type). */
  listingAttributes?: Record<string, unknown> | null;
  className?: string;
}

const UserPriceInsightPanel: React.FC<UserPriceInsightPanelProps> = ({
  itemTitle,
  itemCategory,
  itemType,
  currentPrice,
  onSuggestedPrice,
  onClearPrice,
  isEnabled,
  isLoading,
  error,
  data: priceSuggestion,
  getPriceSuggestion,
  clearSuggestion,
  enforcementMode = false,
  bandLoading = false,
  bandError = null,
  band = null,
  bandRecommendationChipsEnabled = false,
  listingAttributes,
  className,
}) => {
  const shownSuggestionKeyRef = useRef<string | null>(null);
  const acceptedSuggestionKeyRef = useRef<string | null>(null);

  const frictionContext = useMemo(
    () => ({
      category: itemCategory,
      subcategory: itemType || null,
      enforcementActive: enforcementMode,
      chipsEnabled: bandRecommendationChipsEnabled,
    }),
    [itemCategory, itemType, enforcementMode, bandRecommendationChipsEnabled],
  );

  const handleGetInsight = () => {
    if (!isEnabled || !itemTitle || !itemCategory) return;
    getPriceSuggestion(itemTitle, itemCategory, itemType || "", currentPrice, {
      listingAttributes,
    });
  };

  const handleClear = () => {
    capturePriceSuggestionDismissed(frictionContext);
    shownSuggestionKeyRef.current = null;
    acceptedSuggestionKeyRef.current = null;
    clearSuggestion();
    onClearPrice?.();
  };

  useEffect(() => {
    if (enforcementMode || !priceSuggestion || !isEnabled) return;
    const key = `${itemCategory}|${itemType}|${priceSuggestion.suggestedPrice}`;
    if (shownSuggestionKeyRef.current !== key) {
      shownSuggestionKeyRef.current = key;
      capturePriceSuggestionShown(frictionContext);
    }
  }, [
    enforcementMode,
    priceSuggestion,
    isEnabled,
    itemCategory,
    itemType,
    frictionContext,
  ]);

  useEffect(() => {
    if (enforcementMode || !priceSuggestion || !onSuggestedPrice) return;
    const key = `${itemCategory}|${itemType}|${priceSuggestion.suggestedPrice}`;
    onSuggestedPrice(priceSuggestion.suggestedPrice);
    if (acceptedSuggestionKeyRef.current !== key) {
      acceptedSuggestionKeyRef.current = key;
      capturePriceSuggestionAccepted({
        ...frictionContext,
        suggestedPrice: priceSuggestion.suggestedPrice,
        applyMode: "auto",
      });
    }
  }, [
    enforcementMode,
    priceSuggestion,
    onSuggestedPrice,
    itemCategory,
    itemType,
    frictionContext,
  ]);

  if (!isEnabled || !itemTitle || !itemCategory) {
    return null;
  }

  const canGetInsight = Boolean(itemTitle && itemCategory);

  if (enforcementMode) {
    if (bandLoading) {
      return (
        <div
          className={cn(
            "mt-4 rounded-lg border border-blue-100 bg-blue-50/80 p-4",
            className,
          )}
          role="status"
          aria-live="polite"
        >
          <p className="text-sm text-blue-800">Loading market price range…</p>
        </div>
      );
    }

    return (
      <div
        className={cn(
          "mt-4 rounded-lg border border-border bg-secondary p-4",
          className,
        )}
      >
        <SellerBandEnforcementPreview
          itemTitle={itemTitle}
          currentPrice={currentPrice}
          band={band}
          bandError={bandError}
          bandRecommendationChipsEnabled={bandRecommendationChipsEnabled}
          priceSuggestion={priceSuggestion}
          onChipSelect={
            onSuggestedPrice
              ? (price, tier) => {
                  capturePriceChipClicked({
                    ...frictionContext,
                    chipType: tier,
                    price,
                  });
                  onSuggestedPrice(price);
                }
              : undefined
          }
        />
      </div>
    );
  }

  if (!priceSuggestion && !error) {
    return (
      <div className={cn("mt-4 bg-secondary rounded-lg p-4", className)}>
        <div className="flex flex-col items-center space-y-3">
          <p className="text-sm text-text-light">
            Get a price suggestion from typical market ranges for your category
            {itemType ? " and type" : ""} (not from your title alone).
          </p>
          {!canGetInsight && (
            <p className="text-xs text-amber-600">
              Please select a category (and title) to get price insights.
            </p>
          )}
          {canGetInsight && !itemType && (
            <p className="text-xs text-text-light">
              Tip: selecting a type/subcategory narrows the comparison.
            </p>
          )}
          <button
            type="button"
            onClick={handleGetInsight}
            disabled={isLoading || !canGetInsight}
            className={`px-4 py-2 text-sm font-medium text-white rounded-md 
 ${
   isLoading || !canGetInsight
     ? "bg-blue-400 cursor-not-allowed"
     : "bg-blue-600 hover:bg-blue-700"
 }`}
          >
            {isLoading ? "Getting Price Insight..." : "Get Price Insight"}
          </button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("mt-4 bg-secondary rounded-lg p-4", className)}>
        <div className="flex flex-col items-center space-y-3">
          <div className="text-center">
            <p className="text-sm font-medium text-foreground mb-2">{error}</p>
            <p className="text-xs text-text-light">
              You can continue listing your item without a price suggestion.
            </p>
          </div>
          <button
            type="button"
            onClick={handleClear}
            className="px-4 py-2 text-sm font-medium text-foreground bg-surface border border-border rounded-md hover:bg-secondary"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  if (!priceSuggestion) {
    return null;
  }

  const getPriceVarianceClass = (current: number, suggested: number) => {
    const variance = Math.abs((current - suggested) / suggested);
    if (variance <= 0.1) return "text-primary-600";
    if (variance <= 0.2) return "text-yellow-600";
    return "text-red-600";
  };

  return (
    <div className={cn("mt-4 bg-secondary rounded-lg p-4", className)}>
      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <InformationCircleIcon className="h-5 w-5 text-blue-500" />
              <h3 className="text-sm font-medium text-foreground">
                Price suggestion
              </h3>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="text-xs text-text-light hover:text-foreground underline"
            >
              Clear
            </button>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-text-light">Suggested Price:</span>
              <span className="text-base font-semibold text-foreground">
                {formatPrice(priceSuggestion.suggestedPrice)}
              </span>
            </div>

            <div className="flex justify-between items-baseline">
              <span className="text-sm text-text-light">Market Range:</span>
              <span className="text-sm text-foreground">
                {formatPrice(priceSuggestion.priceRange.minPrice)} -{" "}
                {formatPrice(priceSuggestion.priceRange.maxPrice)}
              </span>
            </div>

            {currentPrice != null && (
              <div className="flex justify-between items-baseline">
                <span className="text-sm text-text-light">Your Price:</span>
                <span
                  className={`text-sm font-medium ${getPriceVarianceClass(currentPrice, priceSuggestion.suggestedPrice)}`}
                >
                  {formatPrice(currentPrice)}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="text-sm text-text-light">
          <p>{priceSuggestion.rationale}</p>
        </div>
      </div>
    </div>
  );
};

export default UserPriceInsightPanel;
